import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { guiasPendientesDeRecepcion, monitorRecepcion } from '@/services/recepcionCarga.js';
import {
  CargasPage,
  CargasSection,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
} from '../components/CargasLayout.jsx';

export default function ControlCargaMonitor() {
  const navigate = useNavigate();
  const [sucursalId, setSucursalId] = useState('1');
  const [filter, setFilter] = useState('pendientes');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pendientes: 0, recibidasHoy: 0, conIncidenciaHoy: 0 });

  useEffect(() => {
    guiasPendientesDeRecepcion({ sucursalId }).then(setRows).catch(() => setRows([]));
    monitorRecepcion(sucursalId)
      .then(setStats)
      .catch(() => setStats({ pendientes: 0, recibidasHoy: 0, conIncidenciaHoy: 0 }));
  }, [sucursalId]);

  const filtered = useMemo(() => {
    if (filter === 'pendientes') return rows;
    if (filter === 'incidencias') return rows.filter(x => x.conIncidencia);
    return rows.filter(x => x.recibidaAt);
  }, [rows, filter]);

  return (
    <CargasPage
      title="Control de carga confirmada"
      description="Monitor de pendientes, recibidas y con incidencias por sucursal destino."
      hideHeaderOnCompact
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        <CargasSection
          title="Monitor"
          className="col-span-12 md:col-span-8"
          bodyClassName="min-h-0 space-y-3 p-4"
        >
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-12 md:col-span-4">
              <Field label="Sucursal" htmlFor="sucursal">
                <input
                  id="sucursal"
                  value={sucursalId}
                  onChange={e => setSucursalId(e.target.value)}
                  className={cargasInputClassName}
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-8 flex flex-wrap items-end gap-2">
              <button type="button" className={cargasButtonClassName} onClick={() => setFilter('pendientes')}>
                Pendientes ({stats.pendientes})
              </button>
              <button type="button" className={cargasButtonClassName} onClick={() => setFilter('recibidas')}>
                Recibidas hoy ({stats.recibidasHoy})
              </button>
              <button type="button" className={cargasButtonClassName} onClick={() => setFilter('incidencias')}>
                Con incidencia ({stats.conIncidenciaHoy})
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState title="No hay guías" description="No hay registros para el filtro seleccionado." />
          ) : (
            <CargasTableShell className="max-h-[300px]">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-[rgb(var(--panel))]">
                  <tr>
                    <th className="px-3 py-2">Guía</th>
                    <th className="px-3 py-2">Origen</th>
                    <th className="px-3 py-2">Destino</th>
                    <th className="px-3 py-2">Línea</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Recibida</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(g => (
                    <tr key={g.id} className="border-t border-[rgba(var(--border),0.45)]">
                      <td className="px-3 py-2">{g.numero}</td>
                      <td className="px-3 py-2">{g.origenSucursalId}</td>
                      <td className="px-3 py-2">{g.destinoSucursalId}</td>
                      <td className="px-3 py-2">{g.lineaId || '-'}</td>
                      <td className="px-3 py-2">{g.estadoLogistico}</td>
                      <td className="px-3 py-2">
                        {g.recibidaAt ? new Date(g.recibidaAt).toLocaleString('es-AR') : '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className={cargasButtonClassName}
                          onClick={() => navigate('/cargas/recepcion/nueva')}
                        >
                          {g.recibidaAt ? 'Ver detalle' : 'Ir a recepción'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CargasTableShell>
          )}
        </CargasSection>

        <CargasSection
          title="Indicadores"
          className="col-span-12 md:col-span-4"
          bodyClassName="grid grid-cols-1 gap-2 p-4 text-sm"
        >
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Pendientes <strong className="ml-2">{stats.pendientes}</strong>
          </div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Recibidas hoy <strong className="ml-2">{stats.recibidasHoy}</strong>
          </div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Con incidencia <strong className="ml-2">{stats.conIncidenciaHoy}</strong>
          </div>
        </CargasSection>
      </CargasViewport>
    </CargasPage>
  );
}
